import { API } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import AdminNavigation from '@/components/AdminNavigation';
import CustomerNavigation from '@/components/CustomerNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Fish, Plus, X, AlertTriangle, Calendar, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const severityColors = {
  low: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
};

export default function FishHealth() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const Navigation = isAdmin ? AdminNavigation : CustomerNavigation;

  const [observations, setObservations] = useState([]);
  const [options, setOptions] = useState({ observation_types: [], severity_levels: [], stations: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    station: '',
    area: '',
    observation_type: '',
    observation_label: '',
    severity: 'low',
    description: '',
    fish_count_affected: '',
    observed_at: new Date().toISOString().split('T')[0],
  });

  const fetchData = useCallback(async () => {
    try {
      const [obsRes, optRes] = await Promise.all([
        axios.get(`${API}/fish-health`, { withCredentials: true }),
        axios.get(`${API}/fish-health/options`, { withCredentials: true }),
      ]);
      setObservations(obsRes.data || []);
      setOptions(optRes.data || {});
    } catch (err) {
      console.error('Error fetching fish health data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.station || !form.observation_type) {
      toast.error('Vel stasjon og observasjonstype');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/fish-health`, form, { withCredentials: true });
      toast.success('Observasjon registrert');
      setShowForm(false);
      setForm({ station: '', area: '', observation_type: '', observation_label: '', severity: 'low', description: '', fish_count_affected: '', observed_at: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      toast.error('Kunne ikkje registrere observasjon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/fish-health/${id}`, { withCredentials: true });
      toast.success('Observasjon sletta');
      setObservations(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      toast.error('Kunne ikkje slette');
    }
  };

  const setObservationType = (typeId) => {
    const type = options.observation_types.find(t => t.id === typeId);
    setForm(f => ({ ...f, observation_type: typeId, observation_label: type?.label || '' }));
  };

  const setStation = (stationId) => {
    const station = options.stations.find(s => s.id === stationId);
    setForm(f => ({ ...f, station: stationId, area: station?.area || '' }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Navigation />
      <main className="flex-1 min-h-screen p-4 lg:p-6 overflow-x-hidden">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Fish className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h1 data-testid="fish-health-title" className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Observasjonar</h1>
                <p className="text-sm text-slate-600">Registrer og sjå observasjonar</p>
              </div>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              data-testid="new-observation-btn"
              className={showForm ? 'bg-slate-600 hover:bg-slate-700' : 'bg-teal-600 hover:bg-teal-700'}
            >
              {showForm ? <><X className="w-4 h-4 mr-2" /> Lukk</> : <><Plus className="w-4 h-4 mr-2" /> Ny observasjon</>}
            </Button>
          </div>

          {/* New observation form */}
          {showForm && (
            <Card className="border-slate-200 shadow-md mb-6" data-testid="observation-form">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold text-slate-900">Registrer observasjon</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Station */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Næraste stasjon *</label>
                    <Select value={form.station} onValueChange={setStation}>
                      <SelectTrigger data-testid="station-select" className="border-slate-300">
                        <SelectValue placeholder="Vel stasjon" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.stations.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.label} {s.area && <span className="text-slate-400 ml-1">({s.area})</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Dato for observasjon</label>
                    <input
                      type="date"
                      value={form.observed_at}
                      onChange={e => setForm(f => ({ ...f, observed_at: e.target.value }))}
                      className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid="observation-date"
                    />
                  </div>
                </div>

                {/* Observation type - clickable chips */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Type observasjon *</label>
                  <div className="flex flex-wrap gap-2">
                    {options.observation_types.map(t => (
                      <button
                        key={t.id}
                        onClick={() => setObservationType(t.id)}
                        data-testid={`obs-type-${t.id}`}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          form.observation_type === t.id
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Severity - clickable chips */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Alvorsgrad</label>
                  <div className="flex flex-wrap gap-2">
                    {options.severity_levels.map(s => {
                      const c = severityColors[s.id] || severityColors.low;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setForm(f => ({ ...f, severity: s.id }))}
                          data-testid={`severity-${s.id}`}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                            form.severity === s.id
                              ? `${c.bg} ${c.text} ${c.border} shadow-md ring-2 ring-offset-1 ring-${s.color}-300`
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <span className={`inline-block w-2 h-2 rounded-full ${c.dot} mr-2`} />
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Beskriving (valfritt)</label>
                  <Textarea
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Beskriv observasjonen med eigne ord..."
                    className="border-slate-300 min-h-[80px]"
                    data-testid="observation-description"
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowForm(false)}>Avbryt</Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !form.station || !form.observation_type}
                    data-testid="submit-observation-btn"
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {submitting ? 'Lagrar...' : 'Registrer observasjon'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Observations list */}
          <Card className="border-slate-200 shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-slate-900">
                  Registrerte observasjonar ({observations.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {observations.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Fish className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Ingen observasjonar registrert</p>
                  <p className="text-sm mt-1">Trykk «Ny observasjon» for å registrere</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="observations-list">
                  {observations.map(obs => {
                    const sc = severityColors[obs.severity] || severityColors.low;
                    return (
                      <div
                        key={obs.id}
                        className={`p-4 rounded-xl border ${sc.border} ${sc.bg} transition-all hover:shadow-sm`}
                        data-testid={`observation-${obs.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.text} bg-white border ${sc.border}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                                {obs.observation_label || obs.observation_type}
                              </span>
                              <span className={`text-xs font-medium ${sc.text}`}>
                                {(options.severity_levels.find(s => s.id === obs.severity) || {}).label || obs.severity}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-1">
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {obs.station}</span>
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {obs.observed_at}</span>
                              {obs.user_name && <span>Av: {obs.user_name}</span>}
                            </div>
                            {obs.description && (
                              <p className="text-sm text-slate-700 mt-1">{obs.description}</p>
                            )}
                          </div>
                          {(isAdmin || obs.user_id === user?.id) && (
                            <button
                              onClick={() => handleDelete(obs.id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-white transition-colors"
                              data-testid={`delete-obs-${obs.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
